import {
  BoxCertificateType,
  Manufacturer,
  PreciousMetalsProductsStatus,
  ProductCategory,
  ProductType,
  QualityType
} from '@/pages/precious-metal/products/consts';
import { stringify } from 'qs';
import { request } from '@/utils/request';
import { ClientDocumentDetailDto } from './client-documents';
import { baseUploadSingleFile } from './common/base-upload-file';
import { InstructionEntryType } from './wallet';

export interface PreciousMetalProductImage {
  imageUrl: string;
  imageDisplayUrl: string;
  order: number;
  entityType: string;
  entityId: string;
}

export interface PreciousMetalProductItem {
  id: string;
  status?: keyof typeof PreciousMetalsProductsStatus;
  category: keyof typeof ProductCategory;
  type: keyof typeof ProductType;
  name: {
    [key in LanguageCode]?: string;
  };
  description: {
    [key in LanguageCode]?: string;
  };
  manufacturer: keyof typeof Manufacturer | null;
  purity: string;
  weight: string;
  quality: keyof typeof QualityType | null;
  year: string | null;
  boxCertificate: keyof typeof BoxCertificateType | null;
  productImages: PreciousMetalProductImage[];
  weightUnit: string;
  hasSerial: boolean;
  hasCapsule: boolean;
  isCapsule: boolean;
  isSerialNo: boolean;
  isKeepStockRequired: boolean;
  inventory: number;
  updatedAt?: string;
  errorMessage?: string;
  buyMarkupPercent: number;
  sellMarkupPercent: number;
  buyCostPercent: number;
  sellCostPercent: number;
  buyEnabled: boolean;
  sellEnabled: boolean;
  withdrawalEnabled: boolean;
}

export enum LanguageCode {
  'en' = 'en',
  'zh-Hant' = 'zh-Hant',
  'zh-Hans' = 'zh-Hans',
  'es' = 'es',
  'ja' = 'ja',
  'ru' = 'ru'
}

export interface PreciousMetalProduct {
  category: string;
  type: string;
  name: {
    [key in LanguageCode]?: string;
  };
  description: {
    [key in LanguageCode]?: string;
  };
  thumbnail: string;
  images: string[];
  manufacturer: string;
  productionType: string;
  purity: number;
  weight: number;
  weightUnit: string;
  diameter?: number;
  denomination?: string;
  quality?: string;
  country?: string | null;
  year?: string | null;
  mintage?: number | null;
  hasSerial: boolean;
  hasCapsule: boolean;
  isCapsule: boolean;
  boxCertificate: string;
  isSerialNo: boolean;
  enabled: boolean;
  isKeepStockRequired: boolean;
  inventory: number;
}

type PreciousMetalInstructionClientDocument = ClientDocumentDetailDto & {
  details: {
    [key: string]: ClientDocumentDetailDto;
  };
};

export interface PreciousMetalInstructionAttributes {
  locationName: string;
  locationAddress: string;
  handlerName: string;
  handlerIdPhoto: string;
  handlerIdNumber: string;
  handlerPhoneNumber: string;
  handlerRelationship: string;
  handlerTransferPeerId: string;
  appointmentDateTimeFrom: string;
  appointmentDateTimeTo: string;
}

export interface PreciousMetalInstructionDetail {
  id: string;
  userId: string;
  refId: string;
  intent: string;
  type: InstructionEntryType;
  date: string;
  description: string;
  product: {
    id: string;
    thumbnail: string;
    name: string;
    isSerialNo: boolean;
    weightUnit: string;
  };
  unit: number;
  creditAccountId: string;
  creditAccountName: string;
  creditAccountNumber: string;
  debitAccountId: string;
  debitAccountName: string;
  debitAccountNumber: string;
  amount: number;
  feeAmount: number;
  currency: string;
  rate: string;
  status: string;
  subStatus: string;
  serialNumbers: string[];
  remarks: string;
  total: number;
  // TODO: add intent specific typing
  metadata: Record<string, any>;
  clientDocuments: PreciousMetalInstructionClientDocument[];
  attributes?: PreciousMetalInstructionAttributes;
}

// TypeScript interface for a precious metal position
export interface PreciousMetalPosition {
  id: string;
  productId: string;
  userId: string;
  userName: string;
  userAccountNumber: string;
  rmId?: string;
  rmName?: string;
  productName: string;
  thumbnailUrl: string;
  weightUnit: string;
  weight: number;
  position: number;
  return: number;
  marketValue: number; // in USD
  marketPricePerUnit: number; // in USD
  averageCostPerUnit: number; // in USD
  percentageChange: number;
}

export const PreciousMetalPostDocumentEntityType = 'PRECIOUS_METAL_PRODUCT';

export const preciousMetalPostDocument = (params: { file: File; fileName?: string; entityId?: string }) => {
  const MAX_FILE_SIZE = 5 * 1024 * 1024;
  if (params.file && params.file.size > MAX_FILE_SIZE) {
    throw new Error('File size exceeds 5MB limit');
  }

  return baseUploadSingleFile({
    url: `/operators/upload-document`,
    params: {
      file: params.file,
      entityType: PreciousMetalPostDocumentEntityType,
      entityId: params.entityId
    }
  });
};

export const preciousMetalProductCreate = (params: {
  vendorId: number;
  remarks: string;
  remarkChannel: string;
  _yotpToken: string;
  [key: string]: any;
}) => {
  return request('/precious-metal-products', {
    method: 'POST',
    data: params
  });
};

export const preciousMetalProductEdit = (params: {
  id: string;
  remarks: string;
  remarkChannel: string;
  _yotpToken: string;
  [key: string]: any;
}) => {
  return request(`/precious-metal-products/${params.id}/edit`, {
    method: 'POST',
    data: params
  });
};

export const getPreciousMetalList = (params: any) => {
  return request<{ data: PreciousMetalProductItem[]; total: number }>('/precious-metal-products', {
    method: 'GET',
    params
  });
};

export const getPreciousMetalDetail = (id: string) => {
  return request<PreciousMetalProduct>(`/precious-metal-products/${id}`, {
    method: 'GET',
    getResponse: true
  });
};

export interface updatePreciousMetalStatusItem {
  id: string;
  updatedAt: string;
  showName: string;
  status: string;
}

export const updatePreciousMetalStatus = (params: {
  items: updatePreciousMetalStatusItem[];
  remarks: string;
  remarkChannel: string;
  _yotpToken: string;
}) => {
  return request('/precious-metal-products/batch/status', {
    method: 'POST',
    data: params
  });
};

export const recoverWorkflowPreciousMetal = (params: {
  items: updatePreciousMetalStatusItem[];
  remarks: string;
  remarkChannel: string;
  _yotpToken: string;
}) => {
  return request('/precious-metal-products/recover-workflow', {
    method: 'POST',
    data: params
  });
};

export const startWorkflowPreciousMetal = (params: {
  items: updatePreciousMetalStatusItem[];
  remarks: string;
  remarkChannel: string;
  _yotpToken: string;
}) => {
  return request('/precious-metal-products/start-workflow', {
    method: 'POST',
    data: params
  });
};

export const getPreciousMetalInstructionDetail = (params: { id: string }) => {
  return request<PreciousMetalInstructionDetail>(`/precious-metal/instructions/${params.id}`, {
    method: 'GET',
    getResponse: true
  });
};

// API Request/Response interfaces for precious metal instruction operations
export interface ProcessInstructionParams {
  instructionId: string;
  remarks: string;
  remarkChannel: string;
  _yotpToken: string;
}

export interface PreciousMetalInstructionItem {
  id: string;
  time: string;
  accountNumber: string;
  intent: 'PRECIOUS_METAL_BUY' | 'PRECIOUS_METAL_SELL' | 'PRECIOUS_METAL_WITHDRAWAL';
  productName: string;
  productId: string;
  unit: number;
  amount: number;
  currency: string;
  status: string;
  subStatus: string;
  refId: string;
  remarks: string;
  userId: string;
  userName: string;
  userAccountNumber: string;
  total: number;
  rmId: string;
  rmName: string;
}

export const getPreciousMetalInstructions = (params: any) => {
  return request<{ data: PreciousMetalInstructionItem[]; total: number }>('/precious-metal/instructions', {
    method: 'GET',
    params
  });
};

export interface ProcessBuyInstructionResponse {
  success: boolean;
  message: string;
  data?: {
    instructionId: string;
    status: string;
    updatedAt: string;
  };
}
export interface ProcessBuyInstructionResponse {
  success: boolean;
  message: string;
  data?: {
    instructionId: string;
    status: string;
    updatedAt: string;
  };
}

export interface CompleteBuyInstructionParams {
  userId: string;
  instructionId: string;
  serialNumbers?: string[];
  remarks: string;
  remarkChannel: string;
  _yotpToken: string;
}

export interface CompleteBuyInstructionResponse {
  success: boolean;
  message: string;
  data?: {
    instructionId: string;
    status: string;
    serialNumbers?: string[];
    updatedAt: string;
  };
}

export interface ProcessWithdrawalInstructionParams {
  userId: string;
  instructionId: string;
  remarks: string;
  remarkChannel: string;
  _yotpToken: string;
}

export interface ProcessWithdrawalInstructionResponse {
  success: boolean;
  message: string;
  data?: {
    instructionId: string;
    status: string;
    updatedAt: string;
  };
}

export interface GenerateAcknowledgementParams {
  instructionId: string;
  serialNumbers: string[];
  remarks: string;
  remarkChannel: string;
  _yotpToken: string;
}

export interface GenerateAcknowledgementResponse {
  success: boolean;
  message: string;
  data?: {
    instructionId: string;
    acknowledgementId: string;
    documentUrl: string;
    generatedAt: string;
  };
}

export interface ApproveWithdrawalInstructionParams {
  instructionId: string;
  remarks: string;
  remarkChannel: string;
  _yotpToken: string;
}

export interface CancelWithdrawalInstructionParams extends ApproveWithdrawalInstructionParams {}

export interface ClientDocument {
  url: string;
  fileName: string;
  displayName: string;
  id: string;
  clientDocumentId: string;
  isDeleted: boolean;
  status: string;
  order: number;
}
export interface CompleteWithdrawalInstructionParams {
  instructionId: string;
  remarks: string;
  remarkChannel: string;
  _yotpToken: string;
  files: ClientDocument[];
}

export interface CompleteWithdrawalInstructionResponse {
  success: boolean;
  message: string;
  data?: {
    instructionId: string;
    status: string;
    acknowledgementFileUrl: string;
    updatedAt: string;
  };
}

export interface PreciousMetalPositionParams {
  userId?: string;
  rmName?: string;
  productId?: string;
  productName?: string;
  vendorId?: string;
  current: number;
  pageSize: number;
}
export interface getUserProductSerialNumbersParams {
  userId: string;
  productId: string;
}

// Precious Metal Instruction Operation APIs
export const confirmPurchase = (params: ProcessInstructionParams) => {
  return request<ProcessBuyInstructionResponse>(`/precious-metal/instructions/${params.instructionId}/confirm-purchase`, {
    method: 'POST',
    data: {
      userId: params.userId,
      remarks: params.remarks,
      remarkChannel: params.remarkChannel,
      _yotpToken: params._yotpToken
    }
  });
};

export const completeBuyInstruction = (params: CompleteBuyInstructionParams) => {
  return request<CompleteBuyInstructionResponse>(`/precious-metal/instructions/${params.instructionId}/complete-buy`, {
    method: 'POST',
    data: {
      userId: params.userId,
      serialNumbers: params.serialNumbers,
      remarks: params.remarks,
      remarkChannel: params.remarkChannel,
      _yotpToken: params._yotpToken
    }
  });
};

export const processWithdrawalInstruction = (params: ProcessWithdrawalInstructionParams) => {
  return request<ProcessWithdrawalInstructionResponse>(`/precious-metal/instructions/${params.instructionId}/process-withdrawal`, {
    method: 'POST',
    data: {
      userId: params.userId,
      remarks: params.remarks,
      remarkChannel: params.remarkChannel,
      _yotpToken: params._yotpToken
    }
  });
};

export const generateAcknowledgement = (params: GenerateAcknowledgementParams) => {
  return request<GenerateAcknowledgementResponse>(
    `/precious-metal/instructions/withdrawal/${params.instructionId}/generate-acknowledgement`,
    {
      method: 'POST',
      data: {
        serialNumbers: params.serialNumbers,
        remarks: params.remarks,
        remarkChannel: params.remarkChannel,
        _yotpToken: params._yotpToken
      }
    }
  );
};

export const approveWithdrawalInstruction = (params: ApproveWithdrawalInstructionParams) => {
  return request(`/precious-metal/instructions/withdrawal/${params.instructionId}/approve`, {
    method: 'POST',
    data: params
  });
};

export const cancelWithdrawalInstruction = (params: ProcessInstructionParams) => {
  return request(`/precious-metal/instructions/withdrawal/${params.instructionId}/cancel`, {
    method: 'POST',
    data: params
  });
};

export const completeWithdrawalInstruction = (params: CompleteWithdrawalInstructionParams) => {
  return request(`/precious-metal/instructions/withdrawal/${params.instructionId}/complete`, {
    method: 'POST',
    data: params
  });
};

export const getPreciousMetalPosition = (params: PreciousMetalPositionParams) => {
  const queryString = stringify(params, { arrayFormat: 'brackets' });
  return request<{ data: PreciousMetalPosition[]; total: number }>(`/precious-metal/positions?${queryString}`, {
    method: 'GET'
  });
};

export const getPreciousMetalPositionForSelect = (params: PreciousMetalPositionParams) => {
  return request<{ data: PreciousMetalPosition[]; total: number }>('/precious-metal/positions', {
    method: 'GET',
    params: {
      ...params,
      current: 1,
      pageSize: 10
    },
    getResponse: true
  });
};

export const getUserProductSerialNumbers = (params: getUserProductSerialNumbersParams) => {
  return request<{ data: { serialNumbers: string[] } }>(`/precious-metal/positions/serial-numbers`, {
    method: 'GET',
    getResponse: true,
    params: {
      ...params,
      available: true
    }
  });
};

/**
 * Get pending precious metal instructions count
 * Filters by:
 * - status: NEW, PROCESSING (valid GeneralInstructionStatus values)
 * - subStatus: PENDING_PURCHASE, PENDING_ARRIVAL, PENDING_COMPLETE (pending workflow states)
 */
export async function queryPreciousMetalPendingCount(): Promise<number> {
  const response = await request<{ data: PreciousMetalInstructionItem[]; total: number }>(
    '/precious-metal/instructions',
    {
      method: 'GET',
      params: {
        current: 1,
        pageSize: 1,
        status: ['NEW', 'PROCESSING'],
        subStatus: ['PENDING_PURCHASE', 'PENDING_ARRIVAL', 'PENDING_COMPLETE']
      }
    }
  );
  return response.total ?? 0;
}
